<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { TableHeaderSection, DefaultTable, SearchNoResults } from '@/shared/ui';
  import FunctionalActivitiesTableRow from './FunctionalActivitiesTableRow.vue';
  import { OrderOptions } from '@/shared/lib';
  import { FUNCTIONAL_ACTIVITY_ORDER_OPTIONS } from '@/shared/config';
  import type { FunctionalActivity } from '@/entities/FunctionalActivity';

  const props = withDefaults(
    defineProps<{
      orderOptions?: OrderOptions[];
      activities?: FunctionalActivity[];
      loading?: boolean;
      isFilterActive?: boolean;
      searchTerm?: string;
      selectedCount: number;
      hasSelected: boolean;
      isSelected: (id: number) => boolean;
      selectedItems: number[];
      allSelected: boolean;
      onToggleSelection: (id: number) => void;
      onToggleAll: () => void;
      onOpenDeleteConfirmation?: () => Promise<void>;
      onShowNoSelectionError?: () => void;
    }>(),
    {
      orderOptions: () => [],
      activities: () => [],
      loading: false,
      isFilterActive: false,
      selectedCount: 0,
      hasSelected: false,
      isSelected: () => false,
      selectedItems: () => [],
      allSelected: false,
      onToggleSelection: () => {},
      onToggleAll: () => {},
      onOpenDeleteConfirmation: async () => {},
      onShowNoSelectionError: () => {},
      searchTerm: '',
    }
  );

  const expandedActivityId = ref<number | null>(null);

  const handleToggleExpand = (activityId: number) => {
    expandedActivityId.value = expandedActivityId.value === activityId ? null : activityId;
  };

  defineExpose({
    handleToggleExpand,
    expandedActivityId,
  });

  const emit = defineEmits<{
    'update:sort': [sort: string, order: string];
    'update:filter': [filters: Record<string, unknown>];
    'filter-clicked': [];
    edit: [id: number];
    clone: [id: number];
    delete: [id: number];
    'bulk-delete': [ids: number[]];
  }>();

  const handleActivityToggleSelection = (id: number) => {
    props.onToggleSelection(id);
  };

  const handleActivityToggleAll = () => {
    props.onToggleAll();
  };

  const handleActivityBulkDelete = async () => {
    if (props.selectedItems.length === 0) {
      props.onShowNoSelectionError();
      return;
    }
    await props.onOpenDeleteConfirmation();
  };

  const handleActivitySortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const handleActivityFilter = (filters: Record<string, unknown>) => {
    emit('update:filter', filters);
  };

  const handleActivityFilterClicked = () => {
    emit('filter-clicked');
  };

  const handleActivityEdit = (id: number) => {
    emit('edit', id);
  };

  const handleActivityClone = (id: number) => {
    emit('clone', id);
  };

  const handleActivityDelete = (id: number) => {
    emit('delete', id);
  };

  const orderOptions = computed(() =>
    props.orderOptions.length > 0 ? props.orderOptions : FUNCTIONAL_ACTIVITY_ORDER_OPTIONS
  );

  const shouldShowNoResults = computed(
    () => !props.loading && props.activities.length === 0 && (!!props.searchTerm || props.isFilterActive)
  );
</script>

<template>
  <DefaultTable>
    <TableHeaderSection
      :headers="['Atividades Funcionais', 'Ações', '']"
      :item-count="activities.length"
      item-type="atividades funcionais"
      :has-selected="hasSelected"
      :selected-count="selectedCount"
      :selected-documents="selectedItems"
      :all-selected="allSelected"
      :show-column-headers="activities.length > 0"
      :order-options="orderOptions"
      :is-filter-active="isFilterActive"
      :has-filter="false"
      :delete-disabled="!hasSelected"
      custom-message="Lista de Atividades Funcionais"
      :show-item-count="false"
      :th-class="'pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle'"
      th-checkbox-class="w-20 align-middle text-center"
      @toggle-all="handleActivityToggleAll"
      @update:sort="handleActivitySortOptions"
      @update:filter="handleActivityFilter"
      @filter-clicked="handleActivityFilterClicked"
      @delete="handleActivityBulkDelete"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <FunctionalActivitiesTableRow
        v-for="activity in activities"
        :key="activity.id"
        :activity="activity"
        :is-expanded="expandedActivityId === activity.id"
        :is-selected="props.isSelected(activity.id)"
        @toggle-selection="handleActivityToggleSelection"
        @toggle-expand="handleToggleExpand(activity.id)"
        @edit="handleActivityEdit"
        @clone="handleActivityClone"
        @delete="handleActivityDelete"
      />
      <tr v-if="shouldShowNoResults">
        <td colspan="100">
          <SearchNoResults />
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>
